import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { MapPin, Pencil, Trash2, X } from "lucide-react";
import { MapContainer, Marker, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import { toast } from "sonner";
import "leaflet/dist/leaflet.css";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const emptyDealer = { name: "", address: "", phone: "", latitude: null, longitude: null };
const hasCoordinates = (dealer) => Number.isFinite(dealer.latitude)
  && dealer.latitude >= -90
  && dealer.latitude <= 90
  && Number.isFinite(dealer.longitude)
  && dealer.longitude >= -180
  && dealer.longitude <= 180;

const MapFocus = ({ latitude, longitude }) => {
  const map = useMap();
  useEffect(() => {
    map.flyTo([latitude, longitude], Math.max(map.getZoom(), 15), { duration: 0.7 });
  }, [latitude, longitude, map]);
  return null;
};

export const DealerManager = ({ token }) => {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [dealers, setDealers] = useState([]);
  const [draft, setDraft] = useState(emptyDealer);
  const [editingId, setEditingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const reload = useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/dealers`, { headers });
    setDealers(data.dealers || []);
  }, [headers]);

  useEffect(() => {
    reload().catch(() => toast.error("Bayiler yüklenemedi."));
  }, [reload]);

  const reset = () => {
    setDraft(emptyDealer);
    setEditingId(null);
  };

  const saveDealer = async () => {
    if (!draft.name.trim() || !draft.address.trim() || !hasCoordinates(draft)) {
      toast.error("Bayi adı, açık adres, geçerli enlem ve boylam gerekli.");
      return;
    }
    setIsSaving(true);
    try {
      await axios({
        method: editingId ? "patch" : "post",
        url: `${API}/admin/dealers${editingId ? `/${editingId}` : ""}`,
        data: draft,
        headers,
      });
      toast.success(editingId ? "Bayi güncellendi." : "Bayi eklendi.");
      reset();
      await reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bayi kaydedilemedi.");
    } finally {
      setIsSaving(false);
    }
  };

  const startEdit = (dealer) => {
    setEditingId(dealer.id);
    setDraft({
      name: dealer.name,
      address: dealer.address,
      phone: dealer.phone || "",
      latitude: dealer.latitude,
      longitude: dealer.longitude,
    });
  };

  const removeDealer = async (dealer) => {
    if (!window.confirm(`“${dealer.name}” bayisini silmek istiyor musunuz?`)) return;
    try {
      await axios.delete(`${API}/admin/dealers/${dealer.id}`, { headers });
      if (editingId === dealer.id) reset();
      await reload();
      toast.success("Bayi silindi.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Bayi silinemedi.");
    }
  };

  const updateCoordinate = (field, value) => {
    const coordinate = value === "" ? null : Number(value);
    setDraft((current) => ({
      ...current,
      [field]: Number.isFinite(coordinate) ? coordinate : null,
    }));
  };

  return <div>
    <h1 className="font-heading text-3xl font-bold">Bayi yönetimi</h1>
    <p className="mt-2 text-ink/60">Harita konumu yalnızca aşağıdaki enlem ve boylam değerlerinden alınır. Açık adres bayi detayında gösterilir.</p>

    <div className="mt-6 overflow-hidden rounded-3xl border border-detail">
        <MapContainer center={[41.02, 28.95]} zoom={10} scrollWheelZoom={false} doubleClickZoom={false} touchZoom={false} keyboard={false} zoomControl className="h-96 w-full">
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap contributors" />
          {hasCoordinates(draft) && <MapFocus latitude={draft.latitude} longitude={draft.longitude} />}
          {dealers.filter((dealer) => dealer.id !== editingId).map((dealer) => <Marker key={dealer.id} position={[dealer.latitude, dealer.longitude]} />)}
          {hasCoordinates(draft) && <Marker position={[draft.latitude, draft.longitude]} />}
      </MapContainer>
    </div>

    <div className="mt-5 rounded-3xl border border-detail bg-surface p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[.16em] text-brand">{editingId ? "Bayi düzenleniyor" : "Yeni bayi"}</p>
          <h2 className="mt-1 font-heading text-2xl font-bold">{editingId ? draft.name : "Bayi bilgileri"}</h2>
        </div>
        {editingId && <button type="button" onClick={reset} className="inline-flex items-center gap-2 rounded-xl border border-detail px-4 py-2 text-sm font-semibold"><X className="h-4 w-4" />Vazgeç</button>}
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <label className="text-sm font-semibold">Bayi adı<input value={draft.name} onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))} className="field mt-2" placeholder="Bayi adı" /></label>
        <label className="text-sm font-semibold">Telefon<input value={draft.phone} onChange={(event) => setDraft((current) => ({ ...current, phone: event.target.value }))} className="field mt-2" placeholder="Telefon" /></label>
        <label className="text-sm font-semibold sm:col-span-2">Açık adres<textarea value={draft.address} onChange={(event) => setDraft((current) => ({ ...current, address: event.target.value }))} className="field mt-2 min-h-24" placeholder="Mahalle, cadde, bina numarası, ilçe ve şehir" /></label>
        <label className="text-sm font-semibold">Enlem<input type="number" min="-90" max="90" step="any" value={draft.latitude ?? ""} onChange={(event) => updateCoordinate("latitude", event.target.value)} className="field mt-2" placeholder="41.063255" /></label>
        <label className="text-sm font-semibold">Boylam<input type="number" min="-180" max="180" step="any" value={draft.longitude ?? ""} onChange={(event) => updateCoordinate("longitude", event.target.value)} className="field mt-2" placeholder="28.806962" /></label>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" onClick={saveDealer} disabled={isSaving || !hasCoordinates(draft)} className="inline-flex items-center gap-2 rounded-xl bg-brand px-5 py-3 text-sm font-semibold text-cream disabled:opacity-45"><MapPin className="h-4 w-4" />{isSaving ? "Kaydediliyor…" : editingId ? "Değişiklikleri kaydet" : "Bayi ekle"}</button>
      </div>
      <p className="mt-3 text-xs text-ink/45">{hasCoordinates(draft) ? "Koordinatlar geçerli; konum haritada önizleniyor." : "Geçerli enlem ve boylam girin."}</p>
    </div>

    <div className="mt-8">
      <div className="flex items-end justify-between gap-3"><div><p className="text-xs font-semibold uppercase tracking-[.16em] text-brand">Kayıtlı bayiler</p><h2 className="mt-1 font-heading text-2xl font-bold">{dealers.length} bayi</h2></div></div>
      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        {dealers.map((dealer) => <article key={dealer.id} className={`rounded-2xl border bg-surface p-4 ${editingId === dealer.id ? "border-brand ring-2 ring-brand/10" : "border-detail"}`}>
          <b className="block">{dealer.name}</b>
          <p className="mt-2 text-sm text-ink/60">{dealer.address}</p>
          {dealer.phone && <p className="mt-1 text-sm text-ink/45">{dealer.phone}</p>}
          <div className="mt-4 flex gap-2">
            <button type="button" onClick={() => startEdit(dealer)} className="inline-flex items-center gap-2 rounded-lg bg-brand/10 px-3 py-2 text-sm font-semibold text-brand"><Pencil className="h-4 w-4" />Düzenle</button>
            <button type="button" onClick={() => removeDealer(dealer)} className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-sm font-semibold text-red-600"><Trash2 className="h-4 w-4" />Sil</button>
          </div>
        </article>)}
      </div>
    </div>
  </div>;
};
